import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RootStyled } from './rome-domain-order.styled';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import RomeDomain from '@enterprise/models/dto/rome-domain.response.dto';
import { useEffect, useState } from 'react';
import { useGetAllRomeDomains } from '@/queries/useGetRomeDomain';
import SectorRow from '../SectorRow';
import { useRomeDomainsOrderMutation } from '@/mutations/useRomeDomainMutations';
import UpdateRomeDomainOrderDto from '@models/dto/update-rome-domain-order.dto';

const RomeDomainsOrder = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetAllRomeDomains();
  const { mutate } = useRomeDomainsOrderMutation();

  const [rows, setRows] = useState<RomeDomain[]>([]);
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return; // Item was dropped outside the list

    const updatedRows = [...rows];
    const [movedItem] = updatedRows.splice(result.source.index, 1);
    updatedRows.splice(result.destination.index, 0, movedItem);

    // Calculate the correct rank for each item in the updated list
    updatedRows.forEach((item, index) => {
      item.order = index + 1;
    });

    setRows(updatedRows); // Update the local order

    // Calculate the changed items with their new ranks

    const changedItems = updatedRows.slice(
      Math.min(result.source.index, result.destination.index),
      Math.max(result.source.index, result.destination.index) + 1,
    );
    if (changedItems.length <= 1) return;
    const payload: UpdateRomeDomainOrderDto[] = changedItems.map((item) => ({
      id: item.id,
      order: item.order,
    }));
    mutate(payload); // mutation
  };

  useEffect(() => {
    if (isLoading || !data?.data) return;
    setRows(data?.data.sort((a, b) => a.order - b.order));
  }, [isLoading, data?.data]);
  return (
    <RootStyled>
      <Typography variant="h4" mb={1}>
        {3}. {t('SECTORS_ORDER')}
      </Typography>

      <Typography variant="caption" mb={1}>
        {t('RE_ORDER_SECTORS_HINT')}
      </Typography>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(providedDrop) => {
            return (
              <div
                className="container"
                {...providedDrop.droppableProps}
                ref={providedDrop.innerRef}
              >
                {rows.map((row, index) => (
                  <Draggable
                    key={row.id}
                    data-qa={row.name}
                    draggableId={row.id}
                    index={index}
                  >
                    {(providedDrag, snapshot) => {
                      return (
                        <SectorRow
                          providedDrag={providedDrag}
                          key={row.id}
                          label={row.name}
                          order={row.order}
                        />
                      );
                    }}
                  </Draggable>
                ))}
                {providedDrop.placeholder}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
    </RootStyled>
  );
};

export default RomeDomainsOrder;
